import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Button } from '@shared/design-system/ui'
import {
  actions,
  lessonBar,
  lessonHeading,
  lessonIdentity,
  lessonLayout,
  lessonReading,
  lessonScroll,
  lessonStage,
  playerColumn,
  playerWell,
  upNext,
  upNextTitle,
} from './member-access.css'

type LessonPlayerLayoutProps = {
  advanceKey: string
  title: string
  course: ReactNode
  back?: ReactNode
  video?: (events: { onEnded: () => void }) => ReactNode
  onAdvance?: () => void
  previous?: ReactNode
  next?: ReactNode
  tree: ReactNode
  children?: ReactNode
}

export function LessonPlayerLayout({
  advanceKey,
  title,
  course,
  back,
  video,
  onAdvance,
  previous,
  next,
  tree,
  children,
}: LessonPlayerLayoutProps) {
  const [rail, setRail] = useState<'open' | 'closed'>('open')
  const [countdown, setCountdown] = useState<{ key: string; left: number } | null>(null)
  const left = countdown?.key === advanceKey ? countdown.left : null
  const advanceRef = useRef(onAdvance)
  const advanced = useRef(false)

  useEffect(() => {
    advanceRef.current = onAdvance
  }, [onAdvance])

  useEffect(() => {
    advanced.current = false
  }, [advanceKey])

  useEffect(() => {
    if (left === null) return
    if (left <= 0) {
      if (!advanced.current) {
        advanced.current = true
        advanceRef.current?.()
      }
      return
    }
    const id = window.setTimeout(
      () =>
        setCountdown((current) =>
          current?.key === advanceKey ? { key: advanceKey, left: current.left - 1 } : current,
        ),
      1000,
    )
    return () => window.clearTimeout(id)
  }, [advanceKey, left])

  function goNow() {
    if (advanced.current) return
    advanced.current = true
    setCountdown(null)
    advanceRef.current?.()
  }

  return (
    <div className={lessonLayout({ rail })}>
      <section className={lessonStage}>
        <div className={lessonBar}>
          {back}
          <div className={lessonIdentity}>
            {course}
            <h1 className={lessonHeading}>{title}</h1>
          </div>
          <div className={actions}>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setRail((current) => (current === 'open' ? 'closed' : 'open'))}
            >
              {rail === 'open' ? 'Ocultar aulas' : 'Mostrar aulas'}
            </Button>
            {previous}
            {next}
          </div>
        </div>
        {video ? (
          <div className={playerWell}>
            <div className={playerColumn}>
              {video({
                onEnded: () => {
                  if (!onAdvance) return
                  setCountdown((current) =>
                    current?.key === advanceKey ? current : { key: advanceKey, left: 5 },
                  )
                },
              })}
            </div>
            {left !== null && onAdvance ? (
              <div className={upNext} role="status">
                <p className={upNextTitle}>Próxima aula em {left}s</p>
                <div className={actions}>
                  <Button onClick={goNow}>Ir agora</Button>
                  <Button variant="secondary" onClick={() => setCountdown(null)}>
                    Ficar nesta aula
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
        {children ? <div className={video ? lessonScroll : lessonReading}>{children}</div> : null}
      </section>
      {rail === 'open' ? tree : null}
    </div>
  )
}
